import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../lib/query-keys.js';
import { memberApi } from '../lib/api.js';
import type { MemberSearchParams } from '../types/member.js';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 会員一覧を検索条件+ページネーション付きで取得する react-query フック。
 * @graph-connects memberApi.getList [calls] 会員一覧 API
 */
export function useMemberList(params: MemberSearchParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.member.list(params),
    queryFn: () => memberApi.getList(params),
    // 権限ロード中など environment が確定していない間に投げると、本番権限だけの人が
    // STG 一覧を叩いて 403 を踏む。呼び出し側が確定したことを伝えるまで発火させない。
    enabled: options?.enabled ?? true,
  });
}

/** @graph-connects none */
function guardedId(arthurUserId: number | undefined): number {
  if (arthurUserId === undefined) throw new Error('arthurUserId is required');
  return arthurUserId;
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 会員プロフィール（基本情報・サブスク・プラン・LINE）を取得する react-query フック。
 * @graph-connects memberApi.getProfile [calls] 会員プロフィール API
 */
export function useMemberProfile(arthurUserId: number | undefined) {
  return useQuery({
    queryKey: queryKeys.member.profile(arthurUserId ?? 0),
    queryFn: () => memberApi.getProfile(guardedId(arthurUserId)),
    enabled: arthurUserId !== undefined,
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 会員の決済履歴を取得する react-query フック。
 * @graph-connects memberApi.getPayments [calls] 決済履歴 API
 */
export function useMemberPayments(arthurUserId: number | undefined) {
  return useQuery({
    queryKey: queryKeys.member.payments(arthurUserId ?? 0),
    queryFn: () => memberApi.getPayments(guardedId(arthurUserId)),
    enabled: arthurUserId !== undefined,
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 会員のオーダー履歴を取得する react-query フック。
 * @graph-connects memberApi.getOrders [calls] オーダー履歴 API
 */
export function useMemberOrders(arthurUserId: number | undefined) {
  return useQuery({
    queryKey: queryKeys.member.orders(arthurUserId ?? 0),
    queryFn: () => memberApi.getOrders(guardedId(arthurUserId)),
    enabled: arthurUserId !== undefined,
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 会員のチケット履歴を取得する react-query フック。
 * @graph-connects memberApi.getTickets [calls] チケット履歴 API
 */
export function useMemberTickets(arthurUserId: number | undefined) {
  return useQuery({
    queryKey: queryKeys.member.tickets(arthurUserId ?? 0),
    queryFn: () => memberApi.getTickets(guardedId(arthurUserId)),
    enabled: arthurUserId !== undefined,
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 会員のクーポン履歴を取得する react-query フック。
 * @graph-connects memberApi.getCoupons [calls] クーポン履歴 API
 */
export function useMemberCoupons(arthurUserId: number | undefined) {
  return useQuery({
    queryKey: queryKeys.member.coupons(arthurUserId ?? 0),
    queryFn: () => memberApi.getCoupons(guardedId(arthurUserId)),
    enabled: arthurUserId !== undefined,
  });
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 会員の住所一覧を取得する react-query フック。
 * @graph-connects memberApi.getAddresses [calls] 住所一覧 API
 */
export function useMemberAddresses(arthurUserId: number | undefined) {
  return useQuery({
    queryKey: queryKeys.member.addresses(arthurUserId ?? 0),
    queryFn: () => memberApi.getAddresses(guardedId(arthurUserId)),
    enabled: arthurUserId !== undefined,
  });
}
